"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { rpc } from "#rpc";
import type { RecommendationType } from "@/data/routes/depict.route";

type UseRecommendationsArgs = {
	productIds: string[];
	type: RecommendationType;
	limit?: number;
	/** Disable the query entirely (e.g. while context is loading). */
	enabled?: boolean;
};

/**
 * Fetch Depict recommendations for a given context (cart contents, current
 * PDP, etc.). The server route runs Depict + Centra and returns merged
 * `ProductCard`s in Depict's merchandising order, ready to render.
 *
 * Query key includes the sorted product IDs so cart reorders share a cache
 * entry; each cart-membership change produces a new key.
 */
export const useRecommendations = ({
	productIds,
	type,
	limit,
	enabled = true,
}: UseRecommendationsArgs) => {
	const cleanIds = productIds.filter((id): id is string => Boolean(id));
	const sortedIds = [...cleanIds].sort();

	return useQuery({
		queryKey: ["depict-recommendations", type, sortedIds, limit ?? null],
		queryFn: () =>
			rpc.depict.recommendations({
				productIds: cleanIds,
				type,
				limit,
			}),
		enabled: enabled && cleanIds.length > 0,
		staleTime: 5 * 60 * 1000,
		// When the cart changes (e.g. just-added upsell expands productIds), the
		// query key changes and a new fetch fires. Keep the previous response
		// rendered while it's in flight so the carousel doesn't unmount and the
		// scrollbar/quick-add panel don't blink shut between adds.
		placeholderData: keepPreviousData,
	});
};
